<div class="complete-application-wrapper" [class.rtl-content]="textDirection === 'rtl'">
  <!-- Loading State -->
  @if (loading) {
    <div class="loading-container">
      <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
      <p>Loading your application...</p>
    </div>
  }

  <!-- Submitted State - Thank You Screen -->
  @if (!loading && submitted) {
    <div class="application-container">
      <div class="submitted-screen">
        <div class="success-icon">
          <i class="pi pi-check-circle"></i>
        </div>
        <h2>Thank You!</h2>
        <p class="subtitle">Your application has been submitted successfully.</p>
        <div class="info-card">
          <p>Our team will review your application and get back to you shortly.</p>
          <p>You can close this page now.</p>
        </div>
        <div class="application-ref">
          <span class="label">Application Reference:</span>
          <span class="value">{{ applicationData?.applicationNumber }}</span>
        </div>
      </div>
    </div>
  }

  <!-- Application Form -->
  @if (!loading && applicationData && !submitted) {
    <div class="application-container">
      <!-- Welcome Header -->
      <div class="header">
        <h2>Welcome back, {{ applicationData.businessName }}</h2>
        <p class="subtitle">Application #{{ applicationData.applicationNumber }}</p>
        <div class="status-badge" [ngClass]="applicationData.status?.toLowerCase()">
          {{ getStatusDisplayName(applicationData.status) }}
        </div>
      </div>

      <!-- Admin Message / Requested Changes -->
      @if (requestedChanges) {
        <div class="admin-message-section">
          <div class="message-card">
            <div class="message-header">
              <i class="pi pi-info-circle"></i>
              <h4>Changes Requested</h4>
            </div>
            <p class="message-content">{{ requestedChanges }}</p>
            @if (requestedFields.length > 0) {
              <div class="requested-fields-list">
                <span class="label">Please update the following:</span>
                <div class="field-tags">
                  @for (field of requestedFields; track field) {
                    <span class="field-tag">
                      {{ getFieldDisplayName(field) }}
                    </span>
                  }
                </div>
              </div>
            }
          </div>
        </div>
      }

      <!-- Application Summary (Read-only info) -->
      <div class="summary-section">
        <h3 class="partner-section-title">Application Summary</h3>
        <div class="summary-grid">
          <div class="summary-item">
            <span class="label">Business Name</span>
            <span class="value">{{ applicationData.businessName || '-' }}</span>
          </div>
          <div class="summary-item">
            <span class="label">Email</span>
            <span class="value">{{ applicationData.businessEmail || '-' }}</span>
          </div>
          <div class="summary-item">
            <span class="label">Contact Person</span>
            <span class="value">{{ applicationData.contactPersonFullName || '-' }}</span>
          </div>
          <div class="summary-item">
            <span class="label">Phone</span>
            <span class="value">{{
              applicationData.contactNumber ? '+965 ' + applicationData.contactNumber : '-'
            }}</span>
          </div>
          <div class="summary-item">
            <span class="label">Business Type</span>
            <span class="value">{{ applicationData.businessType || '-' }}</span>
          </div>
          <div class="summary-item">
            <span class="label">Submitted</span>
            <span class="value">{{ applicationData.submittedAt | date: 'medium' }}</span>
          </div>
        </div>
      </div>

      <!-- Editable Fields Section (Only show if there are requested fields) -->
      @if (requestedFields.length > 0) {
        <div class="edit-section">
          <h3 class="partner-section-title">Update Required Information</h3>

          <form [formGroup]="applicationForm">
            <!-- Business Information Fields -->
            @if (
              hasAnyRequestedField([
                'businessName',
                'brandName',
                'businessType',
                'countryId',
                'industryId',
                'categoryId',
              ])
            ) {
              <div class="partner-form-section">
                <h4 class="subsection-title">Business Information</h4>

                @if (isFieldRequested('businessName')) {
                  <div class="form-row">
                    <div class="form-field full-width">
                      <label for="businessName" class="partner-field-label"
                        >Business Name <span class="required">*</span></label
                      >
                      <input
                        id="businessName"
                        pInputText
                        formControlName="businessName"
                        placeholder="Enter business name"
                      />
                    </div>
                  </div>
                }

                @if (isFieldRequested('brandName')) {
                  <div class="form-row">
                    <div class="form-field full-width">
                      <label for="brandName" class="partner-field-label">Brand Name</label>
                      <input
                        id="brandName"
                        pInputText
                        formControlName="brandName"
                        placeholder="Enter brand name"
                      />
                    </div>
                  </div>
                }

                <div class="form-row">
                  @if (isFieldRequested('countryId')) {
                    <div class="form-field">
                      <label for="country" class="partner-field-label">Country</label>
                      <p-dropdown
                        inputId="country"
                        formControlName="country"
                        [options]="countries"
                        optionLabel="nameEn"
                        placeholder="Select country"
                        [filter]="true"
                      >
                      </p-dropdown>
                    </div>
                  }

                  @if (isFieldRequested('businessType')) {
                    <div class="form-field">
                      <label for="businessType" class="partner-field-label">Business Type</label>
                      <p-dropdown
                        inputId="businessType"
                        formControlName="businessType"
                        [options]="businessTypes"
                        optionLabel="nameEn"
                        placeholder="Select business type"
                      >
                      </p-dropdown>
                    </div>
                  }
                </div>

                @if (isFieldRequested('industryId') || isFieldRequested('categoryId')) {
                  <div class="form-row">
                    <div class="form-field full-width">
                      <label for="category" class="partner-field-label">Category</label>
                      <p-dropdown
                        inputId="category"
                        formControlName="category"
                        [options]="categories"
                        optionLabel="nameEn"
                        placeholder="Select category"
                        [filter]="true"
                      >
                      </p-dropdown>
                    </div>
                  </div>
                }
              </div>
            }

            <!-- Contact Information Fields -->
            @if (
              hasAnyRequestedField(['contactPersonFullName', 'businessEmail', 'contactNumber'])
            ) {
              <div class="partner-form-section">
                <h4 class="subsection-title">Contact Information</h4>

                @if (isFieldRequested('contactPersonFullName')) {
                  <div class="form-row">
                    <div class="form-field full-width">
                      <label for="contactPersonFullName" class="partner-field-label"
                        >Contact Person</label
                      >
                      <input
                        id="contactPersonFullName"
                        pInputText
                        formControlName="contactPersonFullName"
                        placeholder="Full name"
                      />
                    </div>
                  </div>
                }

                <div class="form-row">
                  @if (isFieldRequested('businessEmail')) {
                    <div class="form-field">
                      <label for="emailAddress" class="partner-field-label">Email Address</label>
                      <input
                        id="emailAddress"
                        pInputText
                        formControlName="emailAddress"
                        placeholder="email@example.com"
                        type="email"
                      />
                    </div>
                  }

                  @if (isFieldRequested('contactNumber')) {
                    <div class="form-field">
                      <label for="mobileNumber" class="partner-field-label">Mobile Number</label>
                      <div class="phone-input">
                        <span class="country-code">+965</span>
                        <input
                          id="mobileNumber"
                          pInputText
                          formControlName="mobileNumber"
                          placeholder="XXXXXXXX"
                          maxlength="8"
                        />
                      </div>
                    </div>
                  }
                </div>
              </div>
            }

            <!-- Online Business Fields -->
            @if (hasAnyRequestedField(['platfomId', 'platform', 'url', 'otherPlatfom'])) {
              <div class="partner-form-section">
                <h4 class="subsection-title">Online Presence</h4>

                <div class="form-row">
                  @if (isFieldRequested('platfomId') || isFieldRequested('platform')) {
                    <div class="form-field">
                      <label for="platform" class="partner-field-label">Platform</label>
                      <p-dropdown
                        inputId="platform"
                        formControlName="platform"
                        [options]="platforms"
                        optionLabel="nameEn"
                        placeholder="Select platform"
                      >
                      </p-dropdown>
                    </div>
                  }

                  @if (isFieldRequested('url')) {
                    <div class="form-field">
                      <label for="webURL" class="partner-field-label">Website URL</label>
                      <input
                        id="webURL"
                        pInputText
                        formControlName="webURL"
                        placeholder="https://example.com"
                      />
                    </div>
                  }
                </div>

                @if (isFieldRequested('otherPlatfom') && isOtherPlatform) {
                  <div class="form-row">
                    <div class="form-field full-width">
                      <label for="otherPlatform" class="partner-field-label">Other Platform</label>
                      <input
                        id="otherPlatform"
                        pInputText
                        formControlName="otherPlatform"
                        placeholder="Specify platform"
                      />
                    </div>
                  </div>
                }
              </div>
            }

            <!-- Banking Information Fields -->
            @if (
              hasAnyRequestedField([
                'bankName',
                'bankAccountHolderName',
                'bankAccountNumber',
                'iban',
              ])
            ) {
              <div class="partner-form-section">
                <h4 class="subsection-title">Banking Information</h4>

                <div class="form-row">
                  @if (isFieldRequested('bankName')) {
                    <div class="form-field">
                      <label for="bankName" class="partner-field-label">Bank Name</label>
                      <input
                        id="bankName"
                        pInputText
                        formControlName="bankName"
                        placeholder="Enter bank name"
                      />
                    </div>
                  }

                  @if (isFieldRequested('bankAccountHolderName')) {
                    <div class="form-field">
                      <label for="bankAccountHolderName" class="partner-field-label"
                        >Account Holder Name</label
                      >
                      <input
                        id="bankAccountHolderName"
                        pInputText
                        formControlName="bankAccountHolderName"
                        placeholder="Account holder name"
                      />
                    </div>
                  }
                </div>

                <div class="form-row">
                  @if (isFieldRequested('bankAccountNumber')) {
                    <div class="form-field">
                      <label for="bankAccountNumber" class="partner-field-label"
                        >Account Number</label
                      >
                      <input
                        id="bankAccountNumber"
                        pInputText
                        formControlName="bankAccountNumber"
                        placeholder="Account number"
                      />
                    </div>
                  }

                  @if (isFieldRequested('iban')) {
                    <div class="form-field">
                      <label for="iban" class="partner-field-label">IBAN</label>
                      <input id="iban" pInputText formControlName="iban" placeholder="KW..." />
                    </div>
                  }
                </div>
              </div>
            }

            <!-- Branding Fields (matching edit-merchant pattern) -->
            @if (hasAnyRequestedField(['logoUrl', 'featuredImageUrl'])) {
              <div class="partner-form-section">
                <h4 class="subsection-title">Branding</h4>

                <div class="form-row">
                  @if (isFieldRequested('logoUrl')) {
                    <div class="form-field">
                      <label for="logoUpload" class="partner-field-label">Logo</label>
                      <div class="partner-image-upload">
                        <!-- Show new preview if user selected a file, otherwise show existing URL -->
                        @if (logoFile && !applicationForm.get('logoFile')?.hasError('fileType')) {
                          <div class="image-preview">
                            <img [src]="logoFile" alt="Logo preview" />
                          </div>
                        }
                        @if (!logoFile || applicationForm.get('logoFile')?.hasError('fileType')) {
                          @if (logoFileURL) {
                            <div class="image-preview">
                              <img [src]="logoFileURL" alt="Logo" />
                            </div>
                          } @else {
                            <div class="upload-placeholder">
                              <i class="pi pi-image"></i>
                              <span>No logo uploaded</span>
                            </div>
                          }
                        }
                        <input
                          type="file"
                          id="logoUpload"
                          formControlName="logoFile"
                          accept=".jpeg,.png"
                          (change)="onFileSelected($event, 'logoFile')"
                          hidden
                        />
                        <label for="logoUpload" class="partner-upload-btn">
                          <i class="pi pi-upload"></i> Upload Logo
                        </label>
                      </div>
                      @if (applicationForm.get('logoFile')?.hasError('fileType')) {
                        <small class="partner-error-text">{{
                          applicationForm.get('logoFile')?.getError('fileType')?.fileType
                        }}</small>
                      }
                      @if (applicationForm.get('logoFile')?.hasError('fileSizeExceeded')) {
                        <small class="partner-error-text">{{
                          applicationForm.get('logoFile')?.getError('fileSizeExceeded')
                            ?.fileSizeExceeded
                        }}</small>
                      }
                      <small class="partner-hint-text">PNG or JPG, max 500KB</small>
                    </div>
                  }

                  @if (isFieldRequested('featuredImageUrl')) {
                    <div class="form-field">
                      <label for="featuredUpload" class="partner-field-label">Featured Image</label>
                      <div class="partner-image-upload">
                        <!-- Show new preview if user selected a file, otherwise show existing URL -->
                        @if (
                          featuredImageFile &&
                          !applicationForm.get('featuredImage')?.hasError('fileType')
                        ) {
                          <div class="image-preview">
                            <img [src]="featuredImageFile" alt="Featured image preview" />
                          </div>
                        }
                        @if (
                          !featuredImageFile ||
                          applicationForm.get('featuredImage')?.hasError('fileType')
                        ) {
                          @if (featuredImageFileURL) {
                            <div class="image-preview">
                              <img [src]="featuredImageFileURL" alt="Featured image" />
                            </div>
                          } @else {
                            <div class="upload-placeholder">
                              <i class="pi pi-image"></i>
                              <span>No image uploaded</span>
                            </div>
                          }
                        }
                        <input
                          type="file"
                          id="featuredUpload"
                          formControlName="featuredImage"
                          accept=".jpeg,.png"
                          (change)="onFileSelected($event, 'featuredImage')"
                          hidden
                        />
                        <label for="featuredUpload" class="partner-upload-btn">
                          <i class="pi pi-upload"></i> Upload Image
                        </label>
                      </div>
                      @if (applicationForm.get('featuredImage')?.hasError('fileType')) {
                        <small class="partner-error-text">{{
                          applicationForm.get('featuredImage')?.getError('fileType')?.fileType
                        }}</small>
                      }
                      @if (applicationForm.get('featuredImage')?.hasError('fileSizeExceeded')) {
                        <small class="partner-error-text">{{
                          applicationForm.get('featuredImage')?.getError('fileSizeExceeded')
                            ?.fileSizeExceeded
                        }}</small>
                      }
                      <small class="partner-hint-text">PNG or JPG, max 500KB</small>
                    </div>
                  }
                </div>
              </div>
            }

            <!-- Comments Field -->
            @if (isFieldRequested('comments')) {
              <div class="partner-form-section">
                <h4 class="subsection-title">Additional Information</h4>

                <div class="form-row">
                  <div class="form-field full-width">
                    <label for="comments" class="partner-field-label">Comments</label>
                    <textarea
                      id="comments"
                      pInputTextarea
                      formControlName="comments"
                      rows="3"
                      placeholder="Any additional information..."
                    ></textarea>
                  </div>
                </div>
              </div>
            }
          </form>
        </div>
      }

      <!-- Documents Section (Only show if documents are requested) -->
      @if (hasDocumentRequests) {
        <div class="documents-section">
          <h3 class="partner-section-title">Required Documents</h3>

          <div class="upload-grid">
            @for (docType of requestedDocumentTypes; track docType) {
              <div class="upload-item">
                <label [for]="'doc_' + docType" class="upload-label">{{
                  getDocumentDisplayName(docType)
                }}</label>
                <p-fileUpload
                  [inputId]="'doc_' + docType"
                  mode="basic"
                  [name]="docType"
                  chooseIcon="pi pi-upload"
                  chooseLabel="Upload"
                  accept="image/*,application/pdf"
                  [maxFileSize]="1000000"
                  (onSelect)="onDocumentUpload($event, docType)"
                  [auto]="true"
                >
                </p-fileUpload>
              </div>
            }
          </div>

          <!-- Uploaded Documents -->
          @if (documents.length > 0) {
            <div class="existing-documents">
              <h4>Uploaded Documents</h4>
              <div class="document-list">
                @for (doc of documents; track doc.id) {
                  <div class="document-item">
                    <div class="doc-info">
                      <i class="pi pi-file-pdf"></i>
                      <span class="doc-name">{{ getDocumentDisplayName(doc.documentType) }}</span>
                      <span class="doc-file">{{ doc.fileName }}</span>
                    </div>
                    <button
                      class="partner-btn-icon delete"
                      (click)="deleteDocument(doc.id)"
                      title="Delete"
                    >
                      <i class="pi pi-trash"></i>
                    </button>
                  </div>
                }
              </div>
            </div>
          }
        </div>
      }

      <!-- Single Submit Button at Bottom -->
      @if (requestedFields.length > 0) {
        <div class="form-actions submit-section">
          <button
            type="button"
            class="btn btn-primary btn-full-width"
            (click)="onSubmitAll()"
            [disabled]="saving"
          >
            @if (saving) {
              <i class="pi pi-spin pi-spinner"></i>
            }
            {{ saving ? 'Submitting...' : 'Submit Application' }}
          </button>
        </div>
      }

      <!-- No Changes Required Message -->
      @if (requestedFields.length === 0) {
        <div class="no-changes-section">
          <div class="success-message">
            <i class="pi pi-check-circle"></i>
            <h3>No Changes Required</h3>
            <p>Your application is complete. Our team is reviewing it and will be in touch soon.</p>
          </div>
        </div>
      }
    </div>
  }
</div>
